import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { X, Search } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import StoryCardApi from "@/components/editorial/StoryCardApi";
import { fetchStories, fetchStoryFilters } from "@/lib/stories";
import { EDITORIAL_IMAGES } from "@/data/content";

const PAGE_SIZE = 12;

export default function StoriesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    cities: [],
    industries: [],
    themes: [],
    seasons: [],
  });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Read filters from URL
  const city = searchParams.get("city") || "";
  const industry = searchParams.get("industry") || "";
  const theme = searchParams.get("theme") || "";
  const season = searchParams.get("season") || "";
  const search = searchParams.get("q") || "";

  const activeFilters = { city, industry, theme, season, search };

  useEffect(() => {
    fetchStoryFilters().then(setFilters).catch(() => {});
  }, []);

  const load = useCallback(
    async (offset = 0, append = false) => {
      if (offset === 0) setLoading(true);
      else setLoadingMore(true);
      try {
        const params = { limit: PAGE_SIZE, offset };
        if (city) params.city = city;
        if (industry) params.industry = industry;
        if (theme) params.theme = theme;
        if (season) params.season = season;
        if (search) params.search = search;
        const data = await fetchStories(params);
        setTotal(data.total);
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [city, industry, theme, season, search]
  );

  useEffect(() => {
    load(0, false);
  }, [load]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => setSearchParams({}, { replace: true });

  const canLoadMore = !loading && items.length < total;
  const handleLoadMore = () => load(items.length, true);

  const activeFilterEntries = Object.entries(activeFilters).filter(([_, v]) => v);

  return (
    <SiteLayout>
      <></>

      {/* Sub-hero editorial */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden bg-cream">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: `url(${EDITORIAL_IMAGES.production})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(12px)",
          }}
        />
        <div className="absolute inset-0 bg-cream/70" />
        <Container className="relative">
          <Eyebrow tone="terracotta" className="mb-4">Historias</Eyebrow>
          <h1 className="font-serif font-light leading-[1.02] tracking-tight text-[clamp(2.5rem,6vw,5rem)] max-w-4xl">
            Mujeres que están<br />
            <span className="italic">moviendo México.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-charcoal/75 max-w-2xl leading-relaxed">
            Un archivo editorial en crecimiento. Cada historia es un retrato
            documental, una conversación larga y una huella comunitaria
            verificable.
          </p>
        </Container>
      </section>

      {/* Filters sticky */}
      <section
        data-testid="stories-filters"
        className="sticky top-[60px] z-20 bg-cream/95 backdrop-blur-md border-y border-charcoal/10"
      >
        <Container className="py-4">
          <div className="flex flex-wrap gap-3 items-center">
            <FilterSelect
              testid="filter-city"
              value={city}
              onChange={(v) => setParam("city", v)}
              options={filters.cities}
              placeholder="Ciudad"
            />
            <FilterSelect
              testid="filter-industry"
              value={industry}
              onChange={(v) => setParam("industry", v)}
              options={filters.industries}
              placeholder="Industria"
            />
            <FilterSelect
              testid="filter-theme"
              value={theme}
              onChange={(v) => setParam("theme", v)}
              options={filters.themes}
              placeholder="Tema"
            />
            <FilterSelect
              testid="filter-season"
              value={season}
              onChange={(v) => setParam("season", v)}
              options={filters.seasons}
              placeholder="Temporada"
            />
            <div className="relative flex-1 min-w-0 sm:min-w-[180px] max-w-full sm:max-w-xs w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal/40" />
              <input
                data-testid="filter-search"
                type="search"
                placeholder="Buscar…"
                value={search}
                onChange={(e) => setParam("q", e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-charcoal/20 rounded-sm bg-white focus:border-terracotta focus:outline-none"
              />
            </div>
            {activeFilterEntries.length > 0 && (
              <button
                data-testid="clear-filters"
                onClick={clearAll}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-terracotta hover:text-[#A64826]"
              >
                <X className="h-3.5 w-3.5" />
                Limpiar
              </button>
            )}
          </div>
        </Container>
      </section>

      {/* Grid */}
      <section className="bg-cream py-16 md:py-20" data-testid="stories-grid-section">
        <Container>
          {loading ? (
            <p className="text-center text-charcoal/50 py-16">Cargando historias…</p>
          ) : items.length === 0 ? (
            <div
              data-testid="stories-empty"
              className="text-center py-24 max-w-md mx-auto"
            >
              <p className="font-serif italic text-2xl md:text-3xl text-charcoal mb-4">
                No encontramos historias con esos filtros.
              </p>
              <button
                onClick={clearAll}
                className="text-sm text-terracotta underline underline-offset-4 hover:text-[#A64826]"
              >
                Limpiar filtros
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-charcoal/60 mb-8">
                {total} {total === 1 ? "historia" : "historias"}
              </p>
              <div
                data-testid="stories-grid"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
              >
                {items.map((s, i) => (
                  <StoryCardApi key={s.id} story={s} index={i} />
                ))}
              </div>
              {canLoadMore && (
                <div className="flex justify-center mt-14">
                  <button
                    data-testid="load-more"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 rounded-full border border-charcoal/25 px-7 py-3 text-sm hover:bg-charcoal hover:text-cream transition-all duration-[400ms] disabled:opacity-60"
                  >
                    {loadingMore ? "Cargando…" : "Cargar más"}
                  </button>
                </div>
              )}
            </>
          )}
        </Container>
      </section>
    </SiteLayout>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-charcoal/20 bg-white rounded-sm px-3 py-2 text-sm w-full sm:w-auto sm:min-w-[140px]"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}
